import { Form } from "client/Components";
import {
	AddTilesResponseModel,
	GenericDetailsTileModel,
	GenericUpdateTileModel,
	updateGenericTile
} from "client/Api/Workspace/workspace";
import { IFormHandler, useFormHandler } from "client/Forms";
import { createValidationConfiguration, IValidationConfiguration } from "client/Validation";
import {
	doesNotExceedLength,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useImperativeHandle,
	useState
} from "react";
import { AppColorKind } from "client/Api/Apps/common";
import { ExistingTile } from "./common";
import { EditTileHandle } from "./EditTile";
import { EditTileGeneralFields } from "./GeneralFields";
import { useUpdateTile } from "./hooks/useUpdateTile";
import { AppIconModel } from "client_legacy/AppIcon/common";

export type GenericEditFormData = EditTileFromData<GenericUpdateTileModel>;

const validationBuilder: IValidationConfiguration<GenericEditFormData> =
	createValidationConfiguration<GenericEditFormData>();

validationBuilder.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

export type EditGenericTileProps = {
	initialModel: GenericDetailsTileModel;
	mode: ExistingTile;
	onIsLoadingChange(isLoading: boolean): void;
	isLoading: boolean;
	onAfterSave: (response?: AddTilesResponseModel) => void;
};

export const EditGenericTile = forwardRef((props: EditGenericTileProps, ref: Ref<EditTileHandle>): ReactElement => {
	const {
		initialModel,
		mode,
		onIsLoadingChange,
		isLoading,
		onAfterSave,
	} = props;

	const [formData, setFormData] = useState<GenericEditFormData>(initialGenericState(initialModel));
	const formHandler: IFormHandler<GenericEditFormData> = useFormHandler(
		formData,
		setFormData,
		validationBuilder
	);

	const { updateHandler } = useUpdateTile<GenericUpdateTileModel>(
		formHandler,
		updateGenericTile,
		isLoading,
		onIsLoadingChange,
		mode,
		onAfterSave
	);

	useImperativeHandle(
		ref,
		() => ({
			updateHandler
		})
	);

	return (
		<Form
			onSubmit="NONE"
		>
			<EditTileGeneralFields
				initialModel={initialModel}
				formHandler={formHandler}
			/>
		</Form>
	);
});
EditGenericTile.displayName = "EditGenericTile";

export function initialGenericState(initialModel: GenericDetailsTileModel): GenericEditFormData {
	const {
		image,
		imageDisplayInfo,
		shouldHideName
	} = initialModel;

	return {
		color: initialModel.tileColor.kind === AppColorKind.Custom
			? { kind: initialModel.tileColor.kind, value: initialModel.tileColor.value }
			: { kind: initialModel.tileColor.kind },
		dimension: initialModel.dimension,
		image: {
			id: image.id,
			type: image.type,
			renderType: imageDisplayInfo.renderType,
			url: imageDisplayInfo.imageUrl
		},
		name: initialModel.name,
		shouldHideName: shouldHideName.isSet
			? shouldHideName
			: {
				isSet: true,
				value: false
			}
	};
}

export type EditTileFromData<TUpdateTileModel extends GenericUpdateTileModel> = Omit<TUpdateTileModel, "id" | "image" | "shouldHideName">
	& {
		image: AppIconModel;
		shouldHideName: SetOptionalModel<boolean>;
	};
